
export interface Digester {
  onAuthenticate: (WWWAuthenticateHeader: string) => boolean,
  setAuthorization: (xhr: XMLHttpRequest, options: XHROptions) => void
}

export enum Method {
  get = 'GET',
  post = 'POST',
  put = 'PUT',
  delete = 'DELETE',
  head = 'HEAD'
}
export enum ResponseType {
  text = 'text',
  arraybuffer = 'arraybuffer',
  blob = 'blob',
  document = 'document',
  json = 'json'
}
export enum ResultStatus {
  timeout = 'timeout',
  abort = 'abort',
  unauthenticated = 'unauthenticated',
  unauthorized = 'unauthorized',
  nosuccess = 'nosuccess',
  error = 'error',
  parse = 'parse',
  success = 'success'
}
export interface UploadData {
  entity: any,
  mimeType?: string
}

export interface XHRPromiseOptions {
	host?:string,
  url: string,
  method: Method,
  accept?: string,
  data?: UploadData,
  responseType?: ResponseType,
  mimeType?: string,
  timeout?: number,
  cache?: boolean,
  progress?: XHRProgressHandler,
  digester?: Digester,
  credentials?: string,
  noAbort?: boolean
}

export interface XHROptions extends XHRPromiseOptions {
  success: XHRSuccessHandler,
  error?: XHRErrorHandler
}
export type XHRSuccessHandler = (data: any, type: ResponseType, xhr: XMLHttpRequest, method:Method, url: string) => void;
export type XHRErrorHandler = (error: ResultStatus, xhr: XMLHttpRequest, status?: number | any) => void;
export type XHRProgressHandler = (absolute: number, relative: number, done: boolean, resultStatus: ResultStatus, xhr: XMLHttpRequest) => void;

export type XHRResult = { data: any, type: ResponseType, xhr: XMLHttpRequest, method:Method, url: string };
export type XHRError = { error: ResultStatus, xhr: XMLHttpRequest, status?: number | any };

export const xhrPromise = (promiseOptions: XHRPromiseOptions, authenticating?: boolean): XHRPromise => {
  let xhrOptions: XHROptions = Object.assign({
    success: undefined,
    error: undefined
  },
    promiseOptions);
    
  
  let xhrFactory = new XHRFactory( promiseOptions.noAbort );

  let executor: (resolve: (value?: XHRResult) => void, reject: (reason?: any) => void) => void = (resolve, reject) => {
    xhrOptions.success = (data: any, type: ResponseType, xhr: XMLHttpRequest, method:Method, url: string) => resolve({ data: data, type: type, xhr: xhr, method: method, url: url });
    xhrOptions.error = (error: ResultStatus, xhr: XMLHttpRequest, status?: number | any) => reject({ error: error, xhr: xhr, status: status });
    _xhr(xhrFactory, xhrOptions, authenticating)
  };

  return new XHRPromise(executor, xhrFactory);
}

export const xhr = (options: XHROptions, authenticating?: boolean): { abort: () => void } => {
  let xhrFactory = new XHRFactory();
  _xhr(xhrFactory, options, authenticating);
  return xhrFactory;
}

const _xhr = (xhrFactory: XHRFactory, options: XHROptions, authenticating?: boolean): void => {
  
  let xhrReq = xhrFactory.get();

  options.url =  options.url + (options.cache !== true && options.url.indexOf('_=') == -1 ? (options.url.indexOf('?') !== -1 ? '&' : '?') + '_=' + Date.now() : '');

  const handleProgress = (event: ProgressEvent, resultStatus: ResultStatus): void => {
    if (options.progress) {
      let done = event === undefined || xhrReq.readyState === XMLHttpRequest.DONE;
      if (event && event.lengthComputable) {
        let absolute = event.loaded;
        let relative = absolute > 0 ? absolute / event.total : 1;
        options.progress(absolute, relative, done, resultStatus, xhrReq);
      } else {
        let absolute = xhrReq.response ? xhrReq.response.byteLength !== undefined ? xhrReq.response.byteLength : xhrReq.response.length !== undefined ? xhrReq.response.length : 0 : 0;
        let relative = !done ? absolute === 0 ? 0 : undefined : 1;
        options.progress(absolute, relative, done, resultStatus, xhrReq);
      }
    }
  }

  xhrReq.addEventListener("loadstart", (evt) => handleProgress(evt, undefined));
  xhrReq.addEventListener("progress", (evt) => handleProgress(evt, undefined));
  //xhrReq.addEventListener("load", ( evt ) => handleProgress( evt ) );
  //xhrReq.addEventListener("abort", ( evt ) => handleProgress( evt ) );
  //xhrReq.addEventListener("error", ( evt ) => handleProgress( evt ) );
  xhrReq.addEventListener("timeout", (evt) => {
    handleProgress(evt, ResultStatus.timeout);
    options.error(ResultStatus.timeout, xhrReq, 999);
  });
  xhrReq.addEventListener("loadend", (evt) => {
    if (xhrReq.readyState === XMLHttpRequest.DONE && xhrReq.status > 0) {
      if (xhrReq.status >= 200 && xhrReq.status < 300) {
        //completed in success
        let response = xhrReq.response;
        if (evt.loaded > 0 && xhrReq.response === null) {
          //xhrReq.responseType response type does not match the content send back by the server
          handleProgress(evt, ResultStatus.parse);
          options.error(ResultStatus.parse, xhrReq, xhrReq.status);
        } else {
          let responseType: ResponseType = ResponseType.text;
          if (xhrReq.responseType === "" || xhrReq.responseType === "text") {
            responseType = ResponseType.text;
          } else if (xhrReq.responseType === "arraybuffer") {
            responseType = ResponseType.arraybuffer;
          } else if (xhrReq.responseType === "blob") {
            responseType = ResponseType.blob;
          } else if (xhrReq.responseType === "document") {
            responseType = ResponseType.document;
            if (response !== null && response.head && response.body && response.head.innerHTML === '') {
              //most likely a DocumentFragment so unwrap and return what the server sent
              response = response.body.firstChild;
            }
          } else if (xhrReq.responseType === "json") {
            responseType = ResponseType.json;
          }
          handleProgress(evt, ResultStatus.success);
          options.success(response, responseType, xhrReq, options.method, options.url);
        }
      } else {
        // completed in http status error
        if (xhrReq.status === 401 || xhrReq.status === 403) {
          //not authenticated OR not authorized (can happen when authentication was optional and credentials went stale)
          if (options.digester && !authenticating === true && options.digester.onAuthenticate(xhrReq.getResponseHeader('WWW-Authenticate'))) {
            //a digester is present AND this was the first try AND the WWW-Authenticate header was successfully processed by the digester -> try once more
            xhrFactory.refresh();
            _xhr(xhrFactory, options, true);
          } else {
            handleProgress(evt, xhrReq.status === 401 ? ResultStatus.unauthenticated : ResultStatus.unauthorized);
            options.error(xhrReq.status === 401 ? ResultStatus.unauthenticated : ResultStatus.unauthorized, xhrReq, xhrReq.status);
          }
        } else {
          handleProgress(evt, ResultStatus.nosuccess);
          options.error(ResultStatus.nosuccess, xhrReq, xhrReq.status);
        }
      }
    } else {
      //aborted 
      handleProgress(evt, ResultStatus.abort);
      options.error(ResultStatus.abort, xhrReq, 0);
    }
  });

  if (options.mimeType) {
    xhrReq.overrideMimeType(options.mimeType);
  }
  try {
    xhrReq.open(options.method, (options.host!==undefined? options.host : '') + options.url);//always asynchronous

    xhrReq.timeout = options.timeout != null && options.timeout > 0 ? options.timeout : 0;
    xhrReq.responseType = options.responseType ? options.responseType : ResponseType.text;

    if (options.digester) {
      options.digester.setAuthorization(xhrReq, options);
    }
    if (options.data && options.data.mimeType) {
      xhrReq.setRequestHeader('Content-Type', options.data.mimeType);
      //            xhrReq.setRequestHeader( 'Accept', options.data.mimeType );
    }
    if (options.accept) {
      xhrReq.setRequestHeader('Accept', options.accept);
    }
    xhrReq.send(options.data && options.data.entity ? options.data.entity : undefined);
  } catch (x) {
    handleProgress(undefined, ResultStatus.error);
    options.error(ResultStatus.error, xhrReq, x);
  }
}

export class XHRPromise extends Promise<XHRResult> {

  constructor(executor: (resolve: (value?: XHRResult) => void, reject: (reason?: any) => void) => void, private xhrAborter: { abort: () => void, canAbort: () => boolean }) {
    super(executor);
//    this.xhrAborter = xhrAborter;
  }

  public canAbort() : boolean {
    return this.xhrAborter.canAbort();
  }
  public abort(): void {
    this.xhrAborter.abort();
  }
}

class  XHRFactory {
  private xhrReq: XMLHttpRequest;
  
  public constructor( private noAbort?: boolean){
    this.refresh();
  }
  
  public refresh(){
    this.xhrReq = new XMLHttpRequest();
  }
  
  public get() : XMLHttpRequest {
    return this.xhrReq;
  }
  public canAbort() : boolean {
    return this.noAbort!==true;
  }
  public abort() : void {
    if( this.canAbort() ) this.xhrReq.abort();
  }
}


